import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  Text,
  View,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Platform,
  KeyboardAvoidingView,
  Image,
} from 'react-native';
import { useRouter }      from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import { StatusBar }      from 'expo-status-bar';
import AsyncStorage       from '@react-native-async-storage/async-storage';
import { Ionicons }       from '@expo/vector-icons';
import DateTimePicker     from '@react-native-community/datetimepicker';
import * as Haptics       from 'expo-haptics';
import { GoogleSignin, statusCodes } from '@react-native-google-signin/google-signin';
import * as WebBrowser    from 'expo-web-browser';
import * as AuthSession   from 'expo-auth-session';
import { useTranslation } from 'react-i18next';
import HarmoniaLogo       from '../components/HarmoniaLogo';
import GuideUtilisation   from '../components/GuideUtilisation';

if (Platform.OS === 'web') {
  WebBrowser.maybeCompleteAuthSession();
}

const WS_BASE  = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';
const API_BASE = `${WS_BASE}/auth`;

const VIOLET = '#6411A9';

// =====================================================
// CONFIGURATION GOOGLE SIGN-IN (HYBRIDE)
// =====================================================

const GOOGLE_CLIENT_ID_WEB     = '492467723054-m39j327gd1bjr0ipqqdo6ejglrgu69gc.apps.googleusercontent.com';
const GOOGLE_CLIENT_ID_ANDROID = '492467723054-u1duqlk51tnnf80uilf1jpn8li8s1hop.apps.googleusercontent.com';

if (Platform.OS !== 'web') {
  GoogleSignin.configure({
    webClientId: GOOGLE_CLIENT_ID_WEB,
    offlineAccess: false,
  });
}

type AuthMode    = 'login' | 'signup' | 'reset' | 'verify-signup' | 'confirm-google';
type MessageType = 'success' | 'error' | 'info' | 'warning';

interface StatusMessage {
  type:    MessageType;
  text:    string;
  visible: boolean;
}

interface GooglePendingInfo {
  idToken: string;
  name:    string;
  email:   string;
  photo:   string;
}

// =====================================================
// COMPOSANT PRINCIPAL
// =====================================================

export default function LoginPage() {
  const router = useRouter();
  const { t }  = useTranslation();

  const [mode,          setMode]          = useState<AuthMode>('login');
  const [loading,       setLoading]       = useState(false);
  const [statusMessage, setStatusMessage] = useState<StatusMessage>({
    type: 'info', text: '', visible: false,
  });

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [nom,      setNom]      = useState('');
  const [prenom,   setPrenom]   = useState('');

  const [dateNaissance,    setDateNaissance]    = useState<Date>(new Date(2000, 0, 1));
  const [dateNaissanceWeb, setDateNaissanceWeb] = useState<string>('2000-01-01');
  const [showDatePicker,   setShowDatePicker]   = useState(false);
  const [showPassword,     setShowPassword]     = useState(false);
  const [pendingEmail,     setPendingEmail]     = useState('');

  const [googlePendingInfo, setGooglePendingInfo] = useState<GooglePendingInfo | null>(null);

  const [nonce] = useState(() => Math.random().toString(36).substring(2, 15));

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showGuide,     setShowGuide]     = useState(false);

  // =====================================================
  // EXPO AUTH SESSION (WEB)
  // =====================================================

  const redirectUri = AuthSession.makeRedirectUri({ scheme: 'harmonia', path: 'login' });
  const discovery   = AuthSession.useAutoDiscovery('https://accounts.google.com');

  const [request, response, promptAsync] = AuthSession.useAuthRequest(
    {
      clientId:     GOOGLE_CLIENT_ID_WEB,
      redirectUri,
      scopes:       ['openid', 'profile', 'email'],
      responseType: AuthSession.ResponseType.IdToken,
      usePKCE:      false,
      extraParams:  { nonce },
    },
    discovery
  );

  useEffect(() => {
    if (Platform.OS === 'web' && response?.type === 'success') {
      const idToken = response.params.id_token;
      if (idToken) {
        try {
          const base64Url    = idToken.split('.')[1];
          const base64       = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload  = decodeURIComponent(atob(base64).split('').map(c =>
            '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          ).join(''));
          const decoded = JSON.parse(jsonPayload);
          setGooglePendingInfo({ idToken, name: decoded.name || '', email: decoded.email || '', photo: decoded.picture || '' });
          setMode('confirm-google');
        } catch {
          setGooglePendingInfo({ idToken, name: t('login.google.defaultUser'), email: '', photo: '' });
          setMode('confirm-google');
        }
      }
    } else if (Platform.OS === 'web' && response?.type === 'error') {
      showMessage('error', t('login.messages.googleWebFailed'));
    }
  }, [response]);

  useEffect(() => { checkExistingSession(); }, []);

  useEffect(() => {
    if (statusMessage.visible) {
      const timer = setTimeout(() => setStatusMessage(prev => ({ ...prev, visible: false })), 5000);
      return () => clearTimeout(timer);
    }
  }, [statusMessage.visible]);

  useEffect(() => {
    if (mode !== 'signup') setTermsAccepted(false);
  }, [mode]);

  const checkExistingSession = async () => {
    try {
      const raw = await AsyncStorage.getItem('harmonia_session');
      if (raw) {
        const parsed = JSON.parse(raw);
        // ── MIS À JOUR : '/home' n'existe plus (ancien fichier supprimé).
        // La racine des onglets est désormais gérée par app/(tabs)/_layout.tsx,
        // qui fait lui-même sa propre vérification de session. On redirige
        // donc simplement vers le groupe (tabs) — Expo Router résout
        // automatiquement l'écran par défaut (actu).
        if (parsed?.access_token) router.replace('/(tabs)/actu');
      }
    } catch {
      console.log('No existing session');
    }
  };

  const showMessage = (type: MessageType, text: string) => {
    if (Platform.OS !== 'web') {
      Haptics.notificationAsync(
        type === 'error'   ? Haptics.NotificationFeedbackType.Error   :
        type === 'success' ? Haptics.NotificationFeedbackType.Success :
                             Haptics.NotificationFeedbackType.Warning
      );
    }
    setStatusMessage({ type, text, visible: true });
  };

  const formatDateDisplay = (date: Date): string => {
    const day   = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}/${month}/${date.getFullYear()}`;
  };

  const formatDateForAPI = (): string => {
    if (Platform.OS === 'web') return dateNaissanceWeb;
    const day   = dateNaissance.getDate().toString().padStart(2, '0');
    const month = (dateNaissance.getMonth() + 1).toString().padStart(2, '0');
    return `${dateNaissance.getFullYear()}-${month}-${day}`;
  };

  const isValidDate = (dateString: string): boolean => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  };

  // =====================================================
  // ACTIONS AUTHENTIFICATION
  // =====================================================

  const handleSignup = async () => {
    if (!termsAccepted) {
      showMessage('error', t('login.messages.mustAcceptTerms')); return;
    }
    if (!email.trim() || !password.trim() || !nom.trim() || !prenom.trim()) {
      showMessage('error', t('login.messages.fillAllFields')); return;
    }
    if (password.length < 6) {
      showMessage('error', t('login.messages.passwordTooShort')); return;
    }
    const dateForAPI = formatDateForAPI();
    if (!isValidDate(dateForAPI)) {
      showMessage('error', t('login.messages.invalidDate')); return;
    }
    try {
      const chkRes = await fetch(`${WS_BASE}/check-registrations`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      if (chkRes.ok) {
        const chk = await chkRes.json();
        if (chk.registrations_open === false) {
          showMessage('error', chk.registrations_message || t('login.messages.registrationsClosed')); return;
        }
      } else {
        showMessage('error', t('login.messages.cannotVerifyRegistrations')); return;
      }
    } catch {
      showMessage('error', t('login.messages.cannotReachServer')); return;
    }
    setLoading(true);
    showMessage('info', t('login.messages.creatingAccount'));
    try {
      const response = await fetch(API_BASE, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'signup', email: email.trim().toLowerCase(), password,
          nom: nom.trim(), prenom: prenom.trim(), date_naissance: dateForAPI,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('login.messages.signupError'));
      setPendingEmail(email.trim().toLowerCase());
      setMode('verify-signup');
      showMessage('success', t('login.messages.accountCreated'));
    } catch (error: any) {
      showMessage('error', error.message || t('login.messages.cannotCreateAccount'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySignup = async () => {
    setLoading(true);
    showMessage('info', t('login.messages.verifying'));
    try {
      const response = await fetch(API_BASE, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify-email', email: pendingEmail, password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('login.messages.verificationError'));
      if (data.verified) {
        await AsyncStorage.setItem('harmonia_session', JSON.stringify(data.session));
        showMessage('success', t('login.messages.emailVerified'));
        // ── MIS À JOUR : '/home' → '/(tabs)/actu'
        setTimeout(() => router.replace('/(tabs)/actu'), 1500);
      } else {
        showMessage('warning', t('login.messages.emailNotVerified'));
      }
    } catch (error: any) {
      showMessage('error', error.message || t('login.messages.cannotVerifyEmail'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) {
      showMessage('error', t('login.messages.enterEmailPassword')); return;
    }
    setLoading(true);
    showMessage('info', t('login.messages.loggingIn'));
    try {
      const response = await fetch(API_BASE, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'login', email: email.trim().toLowerCase(), password }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (data.error?.includes('Email not confirmed')) {
          showMessage('warning', t('login.messages.emailNotConfirmed')); return;
        }
        throw new Error(data.error || t('login.messages.incorrectCredentials'));
      }
      await AsyncStorage.setItem('harmonia_session', JSON.stringify(data.session));
      showMessage('success', t('login.messages.loginSuccess'));
      // ── MIS À JOUR : '/home' → '/(tabs)/actu'
      setTimeout(() => router.replace('/(tabs)/actu'), 1500);
    } catch (error: any) {
      showMessage('error', error.message || t('login.messages.cannotLogin'));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async () => {
    if (!email.trim()) {
      showMessage('error', t('login.messages.enterEmail')); return;
    }
    setLoading(true);
    showMessage('info', t('login.messages.sendingEmail'));
    try {
      const response = await fetch(API_BASE, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'request-reset', email: email.trim().toLowerCase() }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (data.error?.includes('not found')) {
          showMessage('error', t('login.messages.noAccountWithEmail')); return;
        }
        throw new Error(data.error || t('login.messages.resetRequestError'));
      }
      showMessage('success', t('login.messages.linkSent'));
    } catch (error: any) {
      showMessage('error', error.message || t('login.messages.cannotSendEmail'));
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // GOOGLE SIGNIN
  // =====================================================

  const handleGoogleSignin = async () => {
    if (Platform.OS === 'web') {
      if (!request) return;
      promptAsync();
      return;
    }
    try {
      setLoading(true);
      await GoogleSignin.hasPlayServices();
      const response: any = await GoogleSignin.signIn();
      if (response.type === 'success') {
        const userInfo = response.data;
        if (!userInfo.idToken) throw new Error(t('login.messages.googleTokenMissing'));
        setGooglePendingInfo({
          idToken: userInfo.idToken,
          name:    userInfo.user?.name  || '',
          email:   userInfo.user?.email || '',
          photo:   userInfo.user?.photo || '',
        });
        setMode('confirm-google');
      } else if (response.type === 'cancelled') {
        showMessage('warning', t('login.messages.googleCancelled'));
      } else {
        showMessage('error', t('login.messages.googleFailed'));
      }
    } catch (error: any) {
      if (error.code === statusCodes.SIGN_IN_CANCELLED) {
        showMessage('warning', t('login.messages.googleCancelled'));
      } else if (error.code === statusCodes.IN_PROGRESS) {
        showMessage('info', t('login.messages.googleInProgress'));
      } else if (error.code === statusCodes.PLAY_SERVICES_NOT_AVAILABLE) {
        showMessage('error', t('login.messages.playServicesUnavailable'));
      } else {
        showMessage('error', error.message || t('login.messages.googleError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmGoogle = async () => {
    if (!googlePendingInfo?.idToken) return;
    setLoading(true);
    showMessage('info', t('login.messages.certifyingIdentity'));
    try {
      const response = await fetch(API_BASE, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify-google-token', id_token: googlePendingInfo.idToken }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('login.messages.googleServerValidationFailed'));
      await AsyncStorage.setItem('harmonia_session', JSON.stringify(data.session));
      showMessage('success', t('login.messages.welcomeUser', { name: googlePendingInfo.name }));
      // ── MIS À JOUR : '/home' → '/(tabs)/actu'
      setTimeout(() => router.replace('/(tabs)/actu'), 1000);
    } catch (error: any) {
      showMessage('error', error.message || t('login.messages.googleFinalizeError'));
      setLoading(false);
    }
  };

  // =====================================================
  // DATE PICKER
  // =====================================================

  const onDateChange = (event: any, selectedDate?: Date) => {
    setShowDatePicker(Platform.OS === 'ios');
    if (selectedDate) setDateNaissance(selectedDate);
  };

  const renderDatePicker = () => {
    if (Platform.OS === 'web') {
      return (
        <View style={styles.inputContainer}>
          <Ionicons name="calendar-outline" size={20} color="#999" style={styles.inputIcon} />
          <input
            type="date"
            value={dateNaissanceWeb}
            onChange={(e) => setDateNaissanceWeb(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            min="1900-01-01"
            disabled={loading}
            style={{
              flex: 1, padding: 15, fontSize: 16,
              border: 'none', outline: 'none',
              backgroundColor: 'transparent', color: '#333',
            }}
          />
        </View>
      );
    }
    return (
      <>
        <TouchableOpacity style={styles.inputContainer} onPress={() => setShowDatePicker(true)} disabled={loading}>
          <Ionicons name="calendar-outline" size={20} color="#999" style={styles.inputIcon} />
          <Text style={styles.dateText}>{formatDateDisplay(dateNaissance)}</Text>
          <Ionicons name="chevron-down-outline" size={20} color="#999" />
        </TouchableOpacity>
        {showDatePicker && (
          <DateTimePicker
            value={dateNaissance}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={onDateChange}
            maximumDate={new Date()}
            minimumDate={new Date(1900, 0, 1)}
          />
        )}
      </>
    );
  };

  // =====================================================
  // COMPOSANTS UI RÉUTILISABLES
  // =====================================================

  const renderGoogleButton = () => {
    const googleBlocked = mode === 'signup' && !termsAccepted;
    return (
      <>
        <View style={styles.dividerContainer}>
          <View style={styles.dividerLine} />
          <Text style={styles.dividerText}>{t('login.common.or')}</Text>
          <View style={styles.dividerLine} />
        </View>
        <TouchableOpacity
          style={[styles.googleButton, (loading || googleBlocked) && styles.googleButtonDisabled]}
          onPress={() => {
            if (googleBlocked) {
              showMessage('error', t('login.messages.mustAcceptTerms'));
              return;
            }
            handleGoogleSignin();
          }}
          disabled={loading}
          activeOpacity={googleBlocked ? 1 : 0.8}
        >
          <Image
            source={{ uri: 'https://developers.google.com/identity/images/g-logo.png' }}
            style={styles.googleLogo}
          />
          <Text style={styles.googleButtonText}>{t('login.common.continueWithGoogle')}</Text>
        </TouchableOpacity>
      </>
    );
  };

  const renderTermsCheckbox = () => (
    <TouchableOpacity
      style={styles.checkboxRow}
      onPress={() => setTermsAccepted(!termsAccepted)}
      disabled={loading}
      activeOpacity={0.7}
    >
      <View style={[styles.checkbox, termsAccepted && styles.checkboxChecked]}>
        {termsAccepted && <Ionicons name="checkmark" size={13} color="#fff" />}
      </View>
      <Text style={styles.checkboxText}>
        {t('login.terms.prefix')}
        <Text style={styles.checkboxLink} onPress={(e) => { e.stopPropagation?.(); router.push('/cgu' as any); }}>
          {t('login.terms.cgu')}
        </Text>
        {t('login.terms.middle')}
        <Text style={styles.checkboxLink} onPress={(e) => { e.stopPropagation?.(); router.push('/confidentialite' as any); }}>
          {t('login.terms.privacy')}
        </Text>
        {t('login.terms.and')}
        <Text style={styles.checkboxLink} onPress={(e) => { e.stopPropagation?.(); router.push('/regles-communautaires' as any); }}>
          {t('login.terms.community')}
        </Text>
      </Text>
    </TouchableOpacity>
  );

  // ── Bouton "Besoin d'aide ?" inline — sobre, professionnel ─────────────────
  const renderHelpLink = () => (
    <TouchableOpacity
      style={styles.helpLink}
      onPress={() => setShowGuide(true)}
      disabled={loading}
      activeOpacity={0.7}
    >
      <View style={styles.helpLinkInner}>
        <Ionicons name="help-circle-outline" size={15} color="#9B82C4" />
        <Text style={styles.helpLinkText}>{t('login.common.needHelp')}</Text>
      </View>
    </TouchableOpacity>
  );

  // =====================================================
  // RENDU
  // =====================================================

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      <StatusBar style="light" />

      <LinearGradient colors={['#8A2BE2', '#4B0082']} style={styles.gradientBackground}>

        <GuideUtilisation visible={showGuide} onClose={() => setShowGuide(false)} />

        {statusMessage.visible && (
          <View style={[
            styles.statusMessageContainer,
            styles[`statusMessage${statusMessage.type.charAt(0).toUpperCase() + statusMessage.type.slice(1)}` as keyof typeof styles],
          ]}>
            <Ionicons
              name={
                statusMessage.type === 'success' ? 'checkmark-circle' :
                statusMessage.type === 'error'   ? 'close-circle'     :
                statusMessage.type === 'warning' ? 'warning'          :
                                                   'information-circle'
              }
              size={24} color="#fff" style={styles.statusIcon}
            />
            <Text style={styles.statusMessageText}>{statusMessage.text}</Text>
          </View>
        )}

        {loading && (
          <View style={styles.loadingOverlay}>
            <View style={styles.loadingCard}>
              <ActivityIndicator size="large" color="#8A2BE2" />
              <Text style={styles.loadingText}>{t('login.common.loading')}</Text>
            </View>
          </View>
        )}

        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.logoContainer}>
            <HarmoniaLogo size={80} showText={true} theme="light" />
            <Text style={styles.tagline}>{t('login.common.tagline')}</Text>
          </View>

          <View style={styles.card}>

            {/* ══════════════════════════════════════
                MODE : CONNEXION
            ══════════════════════════════════════ */}
            {mode === 'login' && (
              <>
                <Text style={styles.title}>{t('login.login.title')}</Text>
                <Text style={styles.subtitle}>{t('login.login.subtitle')}</Text>

                <View style={styles.inputContainer}>
                  <Ionicons name="mail-outline" size={20} color="#999" style={styles.inputIcon} />
                  <TextInput
                    style={styles.input}
                    placeholder={t('login.common.emailPlaceholder')}
                    placeholderTextColor="#999"
                    value={email}
                    onChangeText={setEmail}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                    editable={!loading}
                  />
                </View>

                <View style={styles.inputContainer}>
                  <Ionicons name="lock-closed-outline" size={20} color="#999" style={styles.inputIcon} />
                  <TextInput
                    style={styles.input}
                    placeholder={t('login.common.passwordPlaceholder')}
                    placeholderTextColor="#999"
                    value={password}
                    onChangeText={setPassword}
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                    editable={!loading}
                  />
                  <TouchableOpacity onPress={() => setShowPassword(!showPassword)}>
                    <Ionicons name={showPassword ? 'eye-outline' : 'eye-off-outline'} size={20} color="#999" />
                  </TouchableOpacity>
                </View>

                <TouchableOpacity onPress={() => setMode('reset')} disabled={loading}>
                  <Text style={styles.forgotPassword}>{t('login.login.forgotPassword')}</Text>
                </TouchableOpacity>

                <TouchableOpacity onPress={handleLogin} disabled={loading} activeOpacity={0.8}>
                  <LinearGradient colors={['#FF0080', '#FF8C00']} style={styles.primaryButton}>
                    <Text style={styles.buttonText}>{t('login.login.submit')}</Text>
                  </LinearGradient>
                </TouchableOpacity>

                {renderGoogleButton()}

                {/* Liens secondaires */}
                <View style={styles.linksSection}>
                  <TouchableOpacity onPress={() => setMode('signup')} style={styles.linkButton} disabled={loading}>
                    <Text style={styles.linkText}>
                      {t('login.login.noAccountPrefix')} <Text style={styles.linkBold}>{t('login.login.noAccountLink')}</Text>
                    </Text>
                  </TouchableOpacity>

                  <View style={styles.linkSeparator} />
                  {renderHelpLink()}
                </View>
              </>
            )}

            {/* ══════════════════════════════════════
                MODE : INSCRIPTION
            ══════════════════════════════════════ */}
            {mode === 'signup' && (
              <>
                <Text style={styles.title}>{t('login.signup.title')}</Text>
                <Text style={styles.subtitle}>{t('login.signup.subtitle')}</Text>

                <View style={styles.inputContainer}>
                  <Ionicons name="person-outline" size={20} color="#999" style={styles.inputIcon} />
                  <TextInput
                    style={styles.input}
                    placeholder={t('login.signup.lastNamePlaceholder')}
                    placeholderTextColor="#999"
                    value={nom}
                    onChangeText={setNom}
                    autoCapitalize="words"
                    editable={!loading}
                  />
                </View>

                <View style={styles.inputContainer}>
                  <Ionicons name="person-outline" size={20} color="#999" style={styles.inputIcon} />
                  <TextInput
                    style={styles.input}
                    placeholder={t('login.signup.firstNamePlaceholder')}
                    placeholderTextColor="#999"
                    value={prenom}
                    onChangeText={setPrenom}
                    autoCapitalize="words"
                    editable={!loading}
                  />
                </View>

                {renderDatePicker()}

                <View style={styles.inputContainer}>
                  <Ionicons name="mail-outline" size={20} color="#999" style={styles.inputIcon} />
                  <TextInput
                    style={styles.input}
                    placeholder={t('login.common.emailPlaceholder')}
                    placeholderTextColor="#999"
                    value={email}
                    onChangeText={setEmail}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                    editable={!loading}
                  />
                </View>

                <View style={styles.inputContainer}>
                  <Ionicons name="lock-closed-outline" size={20} color="#999" style={styles.inputIcon} />
                  <TextInput
                    style={styles.input}
                    placeholder={t('login.signup.passwordPlaceholder')}
                    placeholderTextColor="#999"
                    value={password}
                    onChangeText={setPassword}
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                    editable={!loading}
                  />
                  <TouchableOpacity onPress={() => setShowPassword(!showPassword)}>
                    <Ionicons name={showPassword ? 'eye-outline' : 'eye-off-outline'} size={20} color="#999" />
                  </TouchableOpacity>
                </View>

                {renderTermsCheckbox()}

                <TouchableOpacity onPress={handleSignup} disabled={loading || !termsAccepted} activeOpacity={0.8}>
                  <LinearGradient
                    colors={termsAccepted ? ['#11998e', '#38ef7d'] : ['#C8C8C8', '#AAAAAA']}
                    style={styles.primaryButton}
                  >
                    <Text style={styles.buttonText}>{t('login.signup.submit')}</Text>
                  </LinearGradient>
                </TouchableOpacity>

                {renderGoogleButton()}

                {/* Liens secondaires */}
                <View style={styles.linksSection}>
                  <TouchableOpacity onPress={() => setMode('login')} style={styles.linkButton} disabled={loading}>
                    <Text style={styles.linkText}>
                      {t('login.signup.alreadyAccountPrefix')} <Text style={styles.linkBold}>{t('login.signup.alreadyAccountLink')}</Text>
                    </Text>
                  </TouchableOpacity>

                  <View style={styles.linkSeparator} />
                  {renderHelpLink()}
                </View>
              </>
            )}

            {/* ══════════════════════════════════════
                MODE : VÉRIFICATION EMAIL
            ══════════════════════════════════════ */}
            {mode === 'verify-signup' && (
              <>
                <Ionicons name="mail-open-outline" size={60} color="#8A2BE2" style={{ alignSelf: 'center', marginBottom: 20 }} />
                <Text style={styles.title}>{t('login.verify.title')}</Text>
                <Text style={styles.subtitle}>
                  {t('login.verify.subtitle')} {'\n'}
                  <Text style={{ fontWeight: 'bold' }}>{pendingEmail}</Text>
                </Text>

                <View style={styles.infoBox}>
                  <Text style={styles.infoText}>
                    {t('login.verify.step1')}{'\n'}
                    {t('login.verify.step2')}{'\n'}
                    {t('login.verify.step3')}
                  </Text>
                </View>

                <TouchableOpacity onPress={handleVerifySignup} disabled={loading} activeOpacity={0.8}>
                  <LinearGradient colors={['#00c6ff', '#0072ff']} style={styles.primaryButton}>
                    <Text style={styles.buttonText}>{t('login.verify.submit')}</Text>
                  </LinearGradient>
                </TouchableOpacity>

                <View style={styles.linksSection}>
                  <TouchableOpacity onPress={() => setMode('signup')} style={styles.linkButton} disabled={loading}>
                    <Text style={styles.linkText}>
                      <Text style={styles.linkBold}>{t('login.verify.backToSignup')}</Text>
                    </Text>
                  </TouchableOpacity>
                </View>
              </>
            )}

            {/* ══════════════════════════════════════
                MODE : MOT DE PASSE OUBLIÉ
            ══════════════════════════════════════ */}
            {mode === 'reset' && (
              <>
                <Ionicons name="key-outline" size={60} color="#FF0080" style={{ alignSelf: 'center', marginBottom: 20 }} />
                <Text style={styles.title}>{t('login.reset.title')}</Text>
                <Text style={styles.subtitle}>{t('login.reset.subtitle')}</Text>

                <View style={styles.inputContainer}>
                  <Ionicons name="mail-outline" size={20} color="#999" style={styles.inputIcon} />
                  <TextInput
                    style={styles.input}
                    placeholder={t('login.common.emailPlaceholder')}
                    placeholderTextColor="#999"
                    value={email}
                    onChangeText={setEmail}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                    editable={!loading}
                  />
                </View>

                <TouchableOpacity onPress={handleRequestReset} disabled={loading} activeOpacity={0.8}>
                  <LinearGradient colors={['#FF0080', '#FF8C00']} style={styles.primaryButton}>
                    <Text style={styles.buttonText}>{t('login.reset.submit')}</Text>
                  </LinearGradient>
                </TouchableOpacity>

                {/* Liens secondaires */}
                <View style={styles.linksSection}>
                  <TouchableOpacity onPress={() => setMode('login')} style={styles.linkButton} disabled={loading}>
                    <Text style={styles.linkText}>
                      <Text style={styles.linkBold}>{t('login.reset.backToLogin')}</Text>
                    </Text>
                  </TouchableOpacity>

                  <View style={styles.linkSeparator} />
                  {renderHelpLink()}
                </View>
              </>
            )}

            {/* ══════════════════════════════════════
                MODE : CONFIRMER GOOGLE
            ══════════════════════════════════════ */}
            {mode === 'confirm-google' && googlePendingInfo && (
              <>
                {googlePendingInfo.photo ? (
                  <Image
                    source={{ uri: googlePendingInfo.photo }}
                    style={{ width: 80, height: 80, borderRadius: 40, alignSelf: 'center', marginBottom: 20 }}
                  />
                ) : (
                  <Ionicons name="person-circle-outline" size={80} color="#8A2BE2" style={{ alignSelf: 'center', marginBottom: 20 }} />
                )}

                <Text style={styles.title}>{t('login.confirmGoogle.title')}</Text>
                <Text style={styles.subtitle}>
                  {t('login.confirmGoogle.subtitle')} {'\n'}
                  <Text style={{ fontWeight: 'bold', color: '#333' }}>{googlePendingInfo.name}</Text> {'\n'}
                  ({googlePendingInfo.email}) ?
                </Text>

                <TouchableOpacity onPress={handleConfirmGoogle} disabled={loading} activeOpacity={0.8}>
                  <LinearGradient colors={['#11998e', '#38ef7d']} style={styles.primaryButton}>
                    <Text style={styles.buttonText}>{t('login.confirmGoogle.submit')}</Text>
                  </LinearGradient>
                </TouchableOpacity>

                <View style={styles.linksSection}>
                  <TouchableOpacity
                    onPress={() => { setMode('login'); setGooglePendingInfo(null); }}
                    style={styles.linkButton}
                    disabled={loading}
                  >
                    <Text style={styles.linkText}>
                      <Text style={styles.linkBold}>{t('login.confirmGoogle.cancel')}</Text>
                    </Text>
                  </TouchableOpacity>
                </View>
              </>
            )}

          </View>

          <View style={{ height: 40 }} />
        </ScrollView>

      </LinearGradient>
    </KeyboardAvoidingView>
  );
}

// =====================================================
// STYLES
// =====================================================
const styles = StyleSheet.create({
  container:          { flex: 1 },
  gradientBackground: { flex: 1 },
  scrollContent: {
    flexGrow: 1, justifyContent: 'center',
    paddingVertical: 40, paddingHorizontal: 20,
  },
  logoContainer: { alignItems: 'center', marginBottom: 40 },
  tagline:       { fontSize: 16, color: '#E0D0FF', fontStyle: 'italic', marginTop: 12, letterSpacing: 1 },
  card: {
    backgroundColor: '#fff', borderRadius: 25, padding: 30,
    shadowColor: '#000', shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.3, shadowRadius: 20, elevation: 10,
  },
  title:    { fontSize: 28, fontWeight: 'bold', color: '#333', marginBottom: 10, textAlign: 'center' },
  subtitle: { fontSize: 14, color: '#666', marginBottom: 30, textAlign: 'center', lineHeight: 20 },

  // ── Inputs ──
  inputContainer: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#F5F5F5',
    borderRadius: 12, paddingHorizontal: 15, marginBottom: 15,
    borderWidth: 1, borderColor: '#E0E0E0',
  },
  inputIcon: { marginRight: 10 },
  input:     { flex: 1, paddingVertical: 15, fontSize: 16, color: '#333' },
  dateText:  { flex: 1, paddingVertical: 15, fontSize: 16, color: '#333' },

  forgotPassword: { color: '#8A2BE2', fontSize: 14, textAlign: 'right', marginBottom: 20, fontWeight: '600' },

  // ── Bouton principal ──
  primaryButton: { paddingVertical: 16, borderRadius: 12, alignItems: 'center', marginTop: 10 },
  buttonText:    { color: '#fff', fontSize: 16, fontWeight: 'bold', textTransform: 'uppercase' },

  // ── Section liens secondaires ──
  linksSection: {
    marginTop: 24,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#F0EAF8',
    alignItems: 'center',
    gap: 0,
  },
  linkButton: { paddingVertical: 6, alignItems: 'center' },
  linkText:   { color: '#666', fontSize: 14 },
  linkBold:   { color: '#8A2BE2', fontWeight: 'bold' },

  // Séparateur entre liens
  linkSeparator: {
    width: 32,
    height: 1,
    backgroundColor: '#EDE8F5',
    marginVertical: 10,
  },

  // ── Bouton aide inline ──
  helpLink: {
    paddingVertical: 4,
    alignItems: 'center',
  },
  helpLinkInner: {
    flexDirection:  'row',
    alignItems:     'center',
    gap:            5,
  },
  helpLinkText: {
    fontSize:      13,
    color:         '#9B82C4',
    fontWeight:    '500',
    letterSpacing: 0.1,
  },

  // ── Info box ──
  infoBox: {
    backgroundColor: '#F0F8FF', borderRadius: 12, padding: 20,
    marginVertical: 20, borderWidth: 1, borderColor: '#B0D4FF',
  },
  infoText: { fontSize: 14, color: '#333', lineHeight: 24 },

  // ── Divider Google ──
  dividerContainer: { flexDirection: 'row', alignItems: 'center', marginVertical: 20 },
  dividerLine:      { flex: 1, height: 1, backgroundColor: '#E0E0E0' },
  dividerText:      { marginHorizontal: 12, color: '#999', fontSize: 14 },

  // ── Google button ──
  googleButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#fff', borderRadius: 12, paddingVertical: 14,
    borderWidth: 1, borderColor: '#E0E0E0',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08, shadowRadius: 4, elevation: 2,
  },
  googleButtonDisabled: { opacity: 0.5 },
  googleLogo:           { width: 22, height: 22, marginRight: 12 },
  googleButtonText:     { fontSize: 16, color: '#333', fontWeight: '600' },

  // ── Status message ──
  statusMessageContainer: {
    position: 'absolute', top: 50, left: 20, right: 20,
    flexDirection: 'row', alignItems: 'center', padding: 15,
    borderRadius: 12, zIndex: 1000,
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25, shadowRadius: 3.84, elevation: 5,
  },
  statusMessageSuccess: { backgroundColor: '#10B981' },
  statusMessageError:   { backgroundColor: '#EF4444' },
  statusMessageWarning: { backgroundColor: '#F59E0B' },
  statusMessageInfo:    { backgroundColor: '#3B82F6' },
  statusIcon:           { marginRight: 10 },
  statusMessageText:    { flex: 1, color: '#fff', fontSize: 14, fontWeight: '600' },

  // ── Loading overlay ──
  loadingOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center', alignItems: 'center', zIndex: 999,
  },
  loadingCard: {
    backgroundColor: '#fff', padding: 30, borderRadius: 20, alignItems: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25, shadowRadius: 3.84, elevation: 5,
  },
  loadingText: { marginTop: 15, fontSize: 16, color: '#333', fontWeight: '600' },

  // ── Checkbox CGU ──
  checkboxRow: {
    flexDirection: 'row', alignItems: 'flex-start',
    gap: 10, marginTop: 6, marginBottom: 16, paddingHorizontal: 2,
  },
  checkbox: {
    width: 22, height: 22, borderRadius: 6,
    borderWidth: 2, borderColor: '#D0C4E8', backgroundColor: '#F8F4FF',
    justifyContent: 'center', alignItems: 'center', flexShrink: 0, marginTop: 1,
  },
  checkboxChecked:  { backgroundColor: '#6411A9', borderColor: '#6411A9' },
  checkboxText:     { flex: 1, fontSize: 11.5, color: '#555', lineHeight: 18 },
  checkboxLink:     { color: '#6411A9', fontWeight: '700', textDecorationLine: 'underline' },
});
